import type {
  ConnectHandoffPayload,
  ConnectHandoffSource,
} from '@/lib/play/connectHandoffTypes'
import { CONNECT_RESUME_RETURN_PATH } from '@/lib/play/connectHandoffTypes'

export const CONNECT_RESUME_REF_STORAGE_KEY = 'wagamour_connect_resume_ref'
export const CONNECT_HANDOFF_SOURCE_STORAGE_KEY =
  'wagamour_connect_handoff_source'
export const CONNECT_HANDOFF_RETURN_PATH_KEY = 'wagamour_connect_handoff_return'
export const PENDING_CONNECT_ONBOARDING_KEY =
  'wagamour_pending_connect_onboarding'
export const PENDING_CONNECT_ACTIVE_RESUME_ID_KEY =
  'wagamour_pending_connect_active_resume_id'
/** Survives handoff clear after Accept so remount/effect rerun cannot wipe CONNECTED. */
export const CONNECT_RESUME_TERMINAL_COMPLETION_KEY =
  'wagamour_connect_resume_terminal'

type StoredHandoff = {
  resumeRef: string
  source: ConnectHandoffSource
  returnPath: string
}

function readSessionHandoff(): StoredHandoff | null {
  if (typeof window === 'undefined') return null
  try {
    const resumeRef = sessionStorage.getItem(CONNECT_RESUME_REF_STORAGE_KEY)
    if (!resumeRef) return null
    const source = (sessionStorage.getItem(
      CONNECT_HANDOFF_SOURCE_STORAGE_KEY
    ) || 'web') as ConnectHandoffSource
    const returnPath =
      sessionStorage.getItem(CONNECT_HANDOFF_RETURN_PATH_KEY) ||
      CONNECT_RESUME_RETURN_PATH
    return { resumeRef, source, returnPath }
  } catch {
    return null
  }
}

function writeSessionHandoff(payload: StoredHandoff): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(CONNECT_RESUME_REF_STORAGE_KEY, payload.resumeRef)
    sessionStorage.setItem(CONNECT_HANDOFF_SOURCE_STORAGE_KEY, payload.source)
    sessionStorage.setItem(CONNECT_HANDOFF_RETURN_PATH_KEY, payload.returnPath)
  } catch {
    // quota / private mode
  }
}

function clearSessionHandoff(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CONNECT_RESUME_REF_STORAGE_KEY)
    sessionStorage.removeItem(CONNECT_HANDOFF_SOURCE_STORAGE_KEY)
    sessionStorage.removeItem(CONNECT_HANDOFF_RETURN_PATH_KEY)
  } catch {
    // ignore
  }
}

/**
 * Browser/PWA/native WebView continuation store — sessionStorage only (no raw token).
 * HttpOnly cookie from POST …/resume is a secondary attach path for same-origin WebView.
 */
export async function storeConnectHandoffPayload(
  payload: ConnectHandoffPayload
): Promise<void> {
  writeSessionHandoff({
    resumeRef: payload.resumeRef,
    source: payload.source,
    returnPath: payload.returnPath,
  })
}

export async function readConnectHandoffPayload(): Promise<ConnectHandoffPayload | null> {
  return readSessionHandoff()
}

export async function clearConnectHandoffPayload(): Promise<void> {
  clearSessionHandoff()
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(PENDING_CONNECT_ACTIVE_RESUME_ID_KEY)
    sessionStorage.removeItem(PENDING_CONNECT_ONBOARDING_KEY)
    // Intentionally keep CONNECT_RESUME_TERMINAL_COMPLETION_KEY — Accept success
    // must survive handoff clear + remount until the user leaves the success UI.
  } catch {
    // ignore
  }
}

export function storeConnectResumeTerminalCompletion(
  code: 'CONNECTED' | 'DECLINED' | 'ALREADY_CONNECTED'
): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(CONNECT_RESUME_TERMINAL_COMPLETION_KEY, code)
  } catch {
    // ignore
  }
}

export function readConnectResumeTerminalCompletion():
  | 'CONNECTED'
  | 'DECLINED'
  | 'ALREADY_CONNECTED'
  | null {
  if (typeof window === 'undefined') return null
  try {
    const value = sessionStorage.getItem(CONNECT_RESUME_TERMINAL_COMPLETION_KEY)
    if (
      value === 'CONNECTED' ||
      value === 'DECLINED' ||
      value === 'ALREADY_CONNECTED'
    ) {
      return value
    }
    return null
  } catch {
    return null
  }
}

export function clearConnectResumeTerminalCompletion(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CONNECT_RESUME_TERMINAL_COMPLETION_KEY)
  } catch {
    // ignore
  }
}

export function markPendingConnectOnboarding(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(PENDING_CONNECT_ONBOARDING_KEY, '1')
  } catch {
    // ignore
  }
}

export function isPendingConnectOnboardingActive(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem(PENDING_CONNECT_ONBOARDING_KEY) === '1'
  } catch {
    return false
  }
}

export function clearPendingConnectOnboarding(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(PENDING_CONNECT_ONBOARDING_KEY)
  } catch {
    // ignore
  }
}

export function storeActivePendingResumeId(resumeId: string): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(PENDING_CONNECT_ACTIVE_RESUME_ID_KEY, resumeId)
  } catch {
    // ignore
  }
}

export function readActivePendingResumeId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return sessionStorage.getItem(PENDING_CONNECT_ACTIVE_RESUME_ID_KEY)
  } catch {
    return null
  }
}

/** Clear stale handoff on logout or account switch. */
export function registerConnectHandoffLogoutClear(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('wagamour:session-cleared', () => {
    void clearConnectHandoffPayload()
    clearConnectResumeTerminalCompletion()
  })
}
